'use client';

import { useEffect } from 'react';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';
import { getStoredUtmData } from '@/hooks/useUtmCapture';
import { VISITOR_ID_COOKIE } from '@/lib/attribution';
import Script from 'next/script';
import { usePathname, useSearchParams } from 'next/navigation';

const GA4_ID_PATTERN = /^G-[A-Z0-9]+$/;
const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/;
const GADS_ID_PATTERN = /^AW-[A-Z0-9]+$/;

function isValidAnalyticsId(id: string, pattern: RegExp): boolean {
  return pattern.test(id) && id.length <= 30;
}

interface GoogleAnalyticsProps {
  measurementId: string;
  debugMode?: boolean;
}

/**
 * Google Analytics 4 Component
 * 
 * Implements GA4 tracking with automatic page view tracking
 * and custom event support.
 */
export function GoogleAnalytics({ measurementId, debugMode = false }: GoogleAnalyticsProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!measurementId || !isValidAnalyticsId(measurementId, GA4_ID_PATTERN) || typeof window === 'undefined') return;

    // Track page views on route change
    const url = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '');
    
    if (window.gtag) {
      window.gtag('config', measurementId, {
        page_path: url,
        debug_mode: debugMode,
      });

      if (debugMode) {
        console.warn('[GA4] Page view tracked:', url);
      }
    }
  }, [pathname, searchParams, measurementId, debugMode]);

  if (!measurementId || !isValidAnalyticsId(measurementId, GA4_ID_PATTERN)) {
    return null;
  }

  return (
    <>
      {/* Google Analytics Script */}
      <Script
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
      />
      <Script
        id="google-analytics"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${measurementId}', {
              page_path: window.location.pathname,
              debug_mode: ${debugMode},
              send_page_view: true
            });
          `,
        }}
      />
    </>
  );
}

/**
 * Google Tag Manager Component
 * 
 * Implements GTM container for managing all tracking tags
 */
interface GoogleTagManagerProps {
  containerId: string;
  debugMode?: boolean;
}

export function GoogleTagManager({ containerId, debugMode = false }: GoogleTagManagerProps) {
  if (!containerId || !isValidAnalyticsId(containerId, GTM_ID_PATTERN)) {
    return null;
  }

  return (
    <>
      {/* Google Tag Manager - Head */}
      <Script
        id="google-tag-manager"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl${debugMode ? '+"&gtm_debug=true"' : ''};f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${containerId}');
          `,
        }}
      />
      
      {/* Google Tag Manager - Body (noscript fallback) */}
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${containerId}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
          title="Google Tag Manager"
        />
      </noscript>
    </>
  );
}

/**
 * Google Ads Conversion Component
 *
 * Injects the gtag.js snippet for Google Ads conversion tracking and remarketing.
 * Safely coexists with GA4 — if dataLayer/gtag are already initialised it only
 * adds the extra `gtag('config', ...)` call rather than re-declaring them.
 */
interface GoogleAdsConversionProps {
  conversionId: string;
  conversionLabel?: string | undefined;
  /** Map of conversion labels keyed by action type (e.g. { submit_lead_form: 'abc', contact_us: 'xyz' }) */
  conversionLabels?: Record<string, string> | undefined;
  debugMode?: boolean | undefined;
}

export function GoogleAdsConversion({
  conversionId,
  conversionLabel,
  conversionLabels,
  debugMode = false,
}: GoogleAdsConversionProps) {
  if (!conversionId || !isValidAnalyticsId(conversionId, GADS_ID_PATTERN)) {
    return null;
  }

  // Build the labels map: merge individual label (as default) with the full map
  const labelsMap: Record<string, string> = { ...conversionLabels };
  if (conversionLabel && !labelsMap.default) {
    labelsMap.default = conversionLabel;
  }

  return (
    <>
      {/* Always load gtag.js for the Google Ads conversion ID so the tag
       * processor is present to handle gtag('event','conversion') calls.
       * ConsentModeDefaults or GTM may have already defined window.gtag
       * (the thin dataLayer-push wrapper), but that alone cannot process
       * conversion events — the external gtag.js script is required. */}
      <Script
        id="google-ads-gtag-js"
        src={`https://www.googletagmanager.com/gtag/js?id=${conversionId}`}
        strategy="afterInteractive"
      />
      <Script
        id="google-ads-conversion"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            if (!window.gtag) {
              function gtag(){dataLayer.push(arguments);}
              window.gtag = gtag;
            }
            window.gtag('js', new Date());
            window.gtag('config', '${conversionId}'${debugMode ? ", { debug_mode: true }" : ''});
            window.__GADS_CONVERSION_ID = '${conversionId}';
            window.__GADS_CONVERSION_LABELS = ${JSON.stringify(labelsMap)};
          `,
        }}
      />
    </>
  );
}

/**
 * Hook for tracking custom events to GA4
 */
export function useAnalyticsEvent() {
  const trackEvent = (eventName: string, parameters?: Record<string, unknown>) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, parameters);
    }
  };

  return { trackEvent };
}

// =============================================================================
// ENHANCED CONVERSIONS
// Hashes user PII (email/phone) with SHA-256 for Google Ads Enhanced Conversions.
// This improves attribution accuracy, especially post-iOS 14.5.
// =============================================================================

async function sha256(value: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(value.trim().toLowerCase());
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Sends hashed user data to Google Ads for Enhanced Conversions.
 * Call this when a lead form is submitted with the user's email/phone.
 */
export async function setEnhancedConversionData(userData: {
  email?: string | undefined;
  phone?: string | undefined;
  firstName?: string | undefined;
  lastName?: string | undefined;
}) {
  if (typeof window === 'undefined' || !window.gtag) return;

  const enhanced: Record<string, string> = {};

  if (userData.email) {
    enhanced.sha256_email_address = await sha256(userData.email);
  }
  if (userData.phone) {
    const digits = userData.phone.replace(/\D/g, '');
    enhanced.sha256_phone_number = await sha256(digits);
  }
  if (userData.firstName) {
    enhanced.sha256_first_name = await sha256(userData.firstName);
  }
  if (userData.lastName) {
    enhanced.sha256_last_name = await sha256(userData.lastName);
  }

  if (Object.keys(enhanced).length > 0) {
    window.gtag('set', 'user_data', enhanced);
  }
}

/**
 * Maps form leadSource values to the action type keys used in the Google Ads
 * conversion labels map. This bridges the gap between how forms identify
 * themselves (e.g. 'property_viewing') and how labels are keyed in the
 * analytics config (e.g. 'submit_lead_form').
 */
const LEAD_SOURCE_TO_ACTION: Record<string, string[]> = {
  contact_form: ['contact_us', 'submit_lead_form'],
  property_enquiry: ['submit_lead_form', 'contact_property'],
  property_viewing: ['submit_lead_form', 'schedule_viewing'],
  agent_meeting: ['submit_lead_form', 'schedule_meeting'],
  click_to_call: ['click_to_call', 'contact_us'],
  click_to_whatsapp: ['click_to_whatsapp', 'contact_us'],
  click_to_email: ['click_to_email', 'contact_us'],
  sign_up: ['sign_up', 'complete_registration'],
};

/**
 * Fires a Google Ads conversion event using the stored conversion ID and labels map.
 *
 * Resolves the label by trying: exact match on actionType, mapped aliases from
 * LEAD_SOURCE_TO_ACTION, then the 'default' fallback.
 *
 * @param actionType - Key to look up (e.g. 'property_viewing', 'submit_lead_form')
 * @param value - Optional monetary value for the conversion
 */
function fireGoogleAdsConversion(actionType: string, value?: number) {
  if (typeof window === 'undefined' || !window.gtag) return;

  const conversionId = window.__GADS_CONVERSION_ID;
  const labels = window.__GADS_CONVERSION_LABELS;
  if (!conversionId) return;

  // Try exact match first, then mapped aliases, then 'default'
  let label = labels?.[actionType];
  if (!label) {
    const aliases = LEAD_SOURCE_TO_ACTION[actionType];
    if (aliases) {
      for (const alias of aliases) {
        if (labels?.[alias]) {
          label = labels[alias];
          break;
        }
      }
    }
  }
  label = label ?? labels?.default;

  const sendTo = label ? `${conversionId}/${label}` : conversionId;

  window.gtag('event', 'conversion', {
    send_to: sendTo,
    ...(value !== undefined && { value, currency: DEFAULT_CURRENCY }),
  });
}

// =============================================================================
// THIRD-PARTY PIXEL EVENT FORWARDING
// Fires standard conversion events to Meta, LinkedIn, TikTok, Pinterest, Twitter
// =============================================================================

function fireMetaEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window === 'undefined' || !window.fbq) return;
  window.fbq('track', eventName, params);
}

function fireLinkedInConversion(conversionId?: string) {
  if (typeof window === 'undefined' || !window.lintrk) return;
  const id = conversionId || window.__LINKEDIN_CONVERSION_ID;
  if (id) {
    window.lintrk('track', { conversion_id: id });
  }
}

function fireTikTokEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window === 'undefined' || !window.ttq) return;
  window.ttq.track(eventName, params);
}

function firePinterestEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window === 'undefined' || !window.pintrk) return;
  window.pintrk('track', eventName, params);
}

function fireTwitterEvent(eventName: string, params?: Record<string, unknown>) {
  if (typeof window === 'undefined' || !window.twq) return;
  window.twq('event', eventName, params);
}

/**
 * Fires a lead/conversion event across all configured marketing pixels.
 * Called automatically from analyticsEvents.generateLead().
 */
function fireAllPixelConversions(leadSource: string, value?: number) {
  const currency = DEFAULT_CURRENCY;

  fireMetaEvent('Lead', {
    content_name: leadSource,
    ...(value !== undefined && { value, currency }),
  });

  fireLinkedInConversion();

  fireTikTokEvent('SubmitForm', {
    content_name: leadSource,
    ...(value !== undefined && { value, currency }),
  });

  firePinterestEvent('lead', {
    lead_type: leadSource,
    ...(value !== undefined && { value, currency }),
  });

  fireTwitterEvent('tw-lead', {
    ...(value !== undefined && { value, currency }),
  });
}

// =============================================================================
// SERVER-SIDE EVENT FORWARDING
// Sends a redundant conversion signal via the server to bypass ad blockers.
// =============================================================================

function generateEventId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

function getGAClientId(): string | undefined {
  const ga = getCookie('_ga');
  if (!ga) return undefined;
  const parts = ga.split('.');
  return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : undefined;
}

/**
 * Read tenant context from the server-rendered JSON script tag.
 * This avoids needing React context in a non-component function.
 */
function getTenantConfig(): { organisationId?: string; siteId?: string } {
  if (typeof document === 'undefined') return {};
  try {
    const el = document.getElementById('tenant-config');
    if (el?.textContent) {
      const parsed = JSON.parse(el.textContent);
      return {
        organisationId: parsed.organisationId,
        siteId: parsed.siteId,
      };
    }
  } catch {
    // Ignore
  }
  return {};
}

function getConsentState(): Record<string, string> | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const prefs = localStorage.getItem('cookie-preferences');
    if (prefs) {
      const parsed = JSON.parse(prefs) as { analytics?: boolean; marketing?: boolean };
      return {
        analytics_storage: parsed.analytics ? 'granted' : 'denied',
        ad_storage: parsed.marketing ? 'granted' : 'denied',
        ad_user_data: parsed.marketing ? 'granted' : 'denied',
        ad_personalization: parsed.marketing ? 'granted' : 'denied',
      };
    }
    const consent = localStorage.getItem('cookie-consent');
    if (consent === 'accepted') {
      return {
        analytics_storage: 'granted',
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
      };
    }
  } catch {
    // Ignore
  }
  return {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  };
}

function fireServerEvent(
  eventName: string,
  params?: Record<string, unknown>,
  userData?: { email?: string; phone?: string; firstName?: string; lastName?: string },
) {
  if (typeof window === 'undefined') return;

  const eventId = generateEventId();
  const visitorId = getCookie(VISITOR_ID_COOKIE);
  const clientId = getGAClientId();
  const tenant = getTenantConfig();
  const consentState = getConsentState();

  // Also set the event_id on the client-side event for deduplication
  if (window.gtag) {
    window.gtag('event', eventName, { ...params, event_id: eventId });
  }

  // Map camelCase client fields to snake_case for the server route
  const mappedUserData = userData
    ? {
        ...(userData.email && { email: userData.email }),
        ...(userData.phone && { phone: userData.phone }),
        ...(userData.firstName && { first_name: userData.firstName }),
        ...(userData.lastName && { last_name: userData.lastName }),
      }
    : undefined;

  // Fire server-side in the background (non-blocking)
  fetch('/api/analytics/events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event_name: eventName,
      event_id: eventId,
      organisation_id: tenant.organisationId,
      site_id: tenant.siteId,
      client_id: clientId,
      visitor_id: visitorId,
      params,
      ...(mappedUserData && { user_data: mappedUserData }),
      ...(consentState && { consent: consentState }),
    }),
    keepalive: true,
  }).catch(() => {
    // Silently fail — this is a redundant signal
  });
}

/**
 * Dispatch a first-party event to Route Royal's collection pipeline.
 * The RouteRoyalTracker component listens for these and batches them.
 * Also pushes to the global queue so events dispatched before the tracker
 * mounts are not lost.
 */
function dispatchRREvent(name: string, params?: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  if (!window.__rr_event_queue) window.__rr_event_queue = [];
  window.__rr_event_queue.push(params ? { name, params } : { name });
  window.dispatchEvent(new CustomEvent('rr:event', { detail: { name, params } }));
}

/**
 * Predefined event tracking functions
 */
export const analyticsEvents = {
  // Page events
  pageView: (url: string, title: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: url,
        page_title: title,
      });
    }
  },

  // Property events
  viewProperty: (propertyId: string, propertyName: string, price?: number) => {
    const visitorId = getCookie(VISITOR_ID_COOKIE);
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'view_item', {
        item_id: propertyId,
        item_name: propertyName,
        value: price,
        currency: DEFAULT_CURRENCY,
        item_category: 'property',
        ...(visitorId && { visitor_id: visitorId }),
      });
    }
    fireMetaEvent('ViewContent', {
      content_ids: [propertyId],
      content_name: propertyName,
      content_type: 'property',
      ...(price !== undefined && { value: price, currency: DEFAULT_CURRENCY }),
    });
    fireTikTokEvent('ViewContent', {
      content_id: propertyId,
      content_name: propertyName,
      ...(price !== undefined && { value: price, currency: DEFAULT_CURRENCY }),
    });
    firePinterestEvent('pagevisit', {
      line_items: [{ product_id: propertyId, product_name: propertyName }],
    });
    dispatchRREvent('property_view', { property_id: propertyId, property_name: propertyName, value: price });
  },

  // Project / development events
  viewProject: (projectId: string, projectName: string, projectType?: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'view_item', {
        item_id: projectId,
        item_name: projectName,
        item_category: 'project',
        ...(projectType && { item_variant: projectType }),
      });
    }
    fireMetaEvent('ViewContent', {
      content_ids: [projectId],
      content_name: projectName,
      content_type: 'project',
    });
    fireTikTokEvent('ViewContent', {
      content_id: projectId,
      content_name: projectName,
    });
    firePinterestEvent('pagevisit', {
      line_items: [{ product_id: projectId, product_name: projectName }],
    });
    dispatchRREvent('development_view', { development_id: projectId, development_name: projectName, project_type: projectType });
  },

  contactProperty: (propertyId: string, propertyName: string, contactMethod: string) => {
    const visitorId = getCookie(VISITOR_ID_COOKIE);
    const params = {
      item_id: propertyId,
      item_name: propertyName,
      contact_method: contactMethod,
      event_category: 'engagement',
      ...(visitorId && { visitor_id: visitorId }),
    };
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'contact_property', params);
    }
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({ event: 'contact_property', ...params });
    }
    fireMetaEvent('Contact', {
      content_name: propertyName,
      content_ids: [propertyId],
    });
    fireTikTokEvent('Contact', {
      content_id: propertyId,
      content_name: propertyName,
    });
    dispatchRREvent('enquiry_submit', { property_id: propertyId, property_name: propertyName, contact_method: contactMethod });
  },

  // Form events
  formSubmit: (formName: string, formLocation: string, propertyId?: string) => {
    const visitorId = getCookie(VISITOR_ID_COOKIE);
    const params = {
      form_name: formName,
      form_location: formLocation,
      event_category: 'engagement',
      ...(visitorId && { visitor_id: visitorId }),
      ...(propertyId && { property_id: propertyId }),
    };

    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'form_submit', params);
    }
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({ event: 'form_submit', ...params });
    }
    dispatchRREvent('form_submit', { form_name: formName, form_location: formLocation, property_id: propertyId });
  },

  formStart: (formName: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'form_start', {
        form_name: formName,
        event_category: 'engagement',
      });
    }
    dispatchRREvent('form_start', { form_name: formName });
  },

  /**
   * Fires when a user opens an enquiry/contact form for a property.
   * Maps to the `begin_checkout` step in the property lead funnel:
   * view_item -> add_to_wishlist -> begin_checkout -> generate_lead -> schedule_meeting
   */
  beginEnquiry: (propertyId: string, propertyName: string, value?: number) => {
    const params = {
      item_id: propertyId,
      item_name: propertyName,
      item_category: 'property',
      event_category: 'conversion_funnel',
      ...(value !== undefined && { value, currency: DEFAULT_CURRENCY }),
    };
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'begin_checkout', params);
    }
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({ event: 'begin_checkout', ...params });
    }
    fireMetaEvent('InitiateCheckout', {
      content_ids: [propertyId],
      content_name: propertyName,
      content_type: 'property',
      ...(value !== undefined && { value, currency: DEFAULT_CURRENCY }),
    });
    dispatchRREvent('enquiry_start', { property_id: propertyId, property_name: propertyName });
  },

  // Search events
  search: (searchTerm: string, resultCount?: number, filters?: Record<string, unknown>) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'search', {
        search_term: searchTerm,
        result_count: resultCount,
        ...filters,
      });
    }
    fireMetaEvent('Search', {
      search_string: searchTerm,
      ...(resultCount !== undefined && { num_items: resultCount }),
      ...filters,
    });
    fireTikTokEvent('Search', {
      query: searchTerm,
      ...filters,
    });
    firePinterestEvent('search', {
      search_query: searchTerm,
    });
    dispatchRREvent('link_click', { action: 'search', search_term: searchTerm, result_count: resultCount, ...filters });
  },

  // Conversion events
  generateLead: (leadSource: string, value?: number, context?: {
    propertyId?: string | undefined;
    propertyName?: string | undefined;
    email?: string | undefined;
    phone?: string | undefined;
    firstName?: string | undefined;
    lastName?: string | undefined;
  }) => {
    const utmData = getStoredUtmData();
    const visitorId = getCookie(VISITOR_ID_COOKIE);

    const eventParams = {
      lead_source: leadSource,
      value: value,
      currency: DEFAULT_CURRENCY,
      ...utmData,
      ...(visitorId && { visitor_id: visitorId }),
      ...(context?.propertyId && { property_id: context.propertyId }),
      ...(context?.propertyName && { property_name: context.propertyName }),
    };

    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'generate_lead', eventParams);

      fireGoogleAdsConversion(leadSource, value);
    }

    fireAllPixelConversions(leadSource, value);

    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'generate_lead',
        ...eventParams,
      });
    }

    // Server-side redundant signal (bypasses ad blockers)
    if (context) {
      const userData: { email?: string; phone?: string; firstName?: string; lastName?: string } = {};
      if (context.email) userData.email = context.email;
      if (context.phone) userData.phone = context.phone;
      if (context.firstName) userData.firstName = context.firstName;
      if (context.lastName) userData.lastName = context.lastName;
      fireServerEvent('generate_lead', eventParams, userData);
    } else {
      fireServerEvent('generate_lead', eventParams);
    }
  },

  // Engagement events
  shareContent: (contentType: string, contentId: string, method: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'share', {
        content_type: contentType,
        content_id: contentId,
        method: method,
      });
    }
    dispatchRREvent('link_click', { action: 'share', content_type: contentType, content_id: contentId, method });
  },

  // Video events (if needed)
  videoStart: (videoId: string, videoTitle: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'video_start', {
        video_id: videoId,
        video_title: videoTitle,
        event_category: 'video',
      });
    }
    dispatchRREvent('link_click', { action: 'video_start', video_id: videoId, video_title: videoTitle });
  },

  // Google Ads conversion tracking
  trackConversion: (conversionId: string, conversionLabel?: string, value?: number, currency?: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'conversion', {
        send_to: conversionLabel ? `${conversionId}/${conversionLabel}` : conversionId,
        ...(value !== undefined && { value, currency: currency || DEFAULT_CURRENCY }),
      });
    }
  },

  // Click-to-call tracking
  clickToCall: (phoneNumber: string, context: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'click_to_call', {
        phone_number: phoneNumber,
        event_category: 'engagement',
        event_label: context,
      });
    }
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'click_to_call',
        phone_number: phoneNumber,
        context,
      });
    }
    fireMetaEvent('Contact', { content_category: 'phone_call', content_name: context });
    fireTikTokEvent('Contact', { content_name: context });
    fireGoogleAdsConversion('click_to_call');
    dispatchRREvent('cta_click', { action: 'click_to_call', phone_number: phoneNumber, context });
  },

  // Click-to-email tracking
  clickToEmail: (emailAddress: string, context: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'click_to_email', {
        email_address: emailAddress,
        event_category: 'engagement',
        event_label: context,
      });
    }
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'click_to_email',
        email_address: emailAddress,
        context,
      });
    }
    fireMetaEvent('Contact', { content_category: 'email', content_name: context });
    fireTikTokEvent('Contact', { content_name: context });
    dispatchRREvent('cta_click', { action: 'click_to_email', email_address: emailAddress, context });
  },

  // Click-to-WhatsApp tracking
  clickToWhatsApp: (phoneNumber: string, context: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'click_to_whatsapp', {
        phone_number: phoneNumber,
        event_category: 'engagement',
        event_label: context,
      });
    }
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'click_to_whatsapp',
        phone_number: phoneNumber,
        context,
      });
    }
    fireMetaEvent('Contact', { content_category: 'whatsapp', content_name: context });
    fireTikTokEvent('Contact', { content_name: context });
    fireGoogleAdsConversion('click_to_whatsapp');
    dispatchRREvent('cta_click', { action: 'click_to_whatsapp', phone_number: phoneNumber, context });
  },

  // User registration / sign-up
  signUp: (method: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'sign_up', {
        method,
      });
    }
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'sign_up',
        method,
      });
    }
    fireMetaEvent('CompleteRegistration', { content_name: method });
    fireTikTokEvent('CompleteRegistration', { content_name: method });
  },

  // Add to wishlist
  addToWishlist: (entityType: string, entityId: string, entityName?: string, value?: number) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'add_to_wishlist', {
        item_id: entityId,
        item_category: entityType,
        ...(entityName && { item_name: entityName }),
        ...(value !== undefined && { value, currency: DEFAULT_CURRENCY }),
      });
    }
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'add_to_wishlist',
        item_id: entityId,
        item_category: entityType,
        ...(entityName && { item_name: entityName }),
        ...(value !== undefined && { value, currency: DEFAULT_CURRENCY }),
      });
    }
    fireMetaEvent('AddToWishlist', {
      content_ids: [entityId],
      content_type: entityType,
      ...(entityName && { content_name: entityName }),
      ...(value !== undefined && { value, currency: DEFAULT_CURRENCY }),
    });
    const normalisedType = entityType.toLowerCase();
    dispatchRREvent(`${normalisedType}_save`, { [`${normalisedType}_id`]: entityId, entity_name: entityName });
  },

  // Schedule meeting / viewing (modal opened)
  scheduleMeetingIntent: (agentName: string, context: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'schedule_meeting_intent', {
        agent_name: agentName,
        event_category: 'engagement',
        event_label: context,
      });
    }
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'schedule_meeting_intent',
        agent_name: agentName,
        context,
      });
    }
    dispatchRREvent('cta_click', { action: 'schedule_meeting', agent_name: agentName, context });
  },

  // Property list view — for remarketing audience building
  propertyListView: (properties: Array<{ id: string; name: string; price?: number }>, listContext?: string) => {
    if (typeof window === 'undefined') return;
    const items = properties.map((p) => ({
      item_id: p.id,
      item_name: p.name,
      ...(p.price !== undefined && { price: p.price }),
      item_category: 'property',
    }));
    if (window.gtag) {
      window.gtag('event', 'view_item_list', {
        item_list_name: listContext || 'property_listing',
        items,
      });
    }
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'view_item_list',
        item_list_name: listContext || 'property_listing',
        items,
      });
    }
    fireMetaEvent('ViewContent', {
      content_type: 'property_listing',
      content_ids: properties.map((p) => p.id),
    });
    // Search/results impression (multiple properties). This is distinct from a
    // single listing entity view (`listing_view` with a `listing_id`), so it
    // uses its own event name to avoid colliding with per-listing analytics.
    dispatchRREvent('results_view', { list_context: listContext, property_count: properties.length, property_ids: properties.slice(0, 10).map(p => p.id) });
  },

  // Custom event
  custom: (eventName: string, parameters: Record<string, unknown>) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, parameters);
    }
    dispatchRREvent(eventName, parameters);
  },
};

// Type declarations for gtag and third-party pixels
declare global {
  interface Window {
    gtag?: (...args: [string, ...unknown[]]) => void;
    dataLayer?: unknown[];
    /** Google Ads conversion ID (e.g. AW-17973100167) */
    __GADS_CONVERSION_ID?: string;
    /** Map of conversion labels keyed by action type */
    __GADS_CONVERSION_LABELS?: Record<string, string>;
    /** LinkedIn conversion ID for lead tracking */
    __LINKEDIN_CONVERSION_ID?: string;
    /** Meta/Facebook Pixel */
    fbq?: (command: string, event: string, params?: Record<string, unknown>) => void;
    /** LinkedIn Insight */
    lintrk?: (command: string, params?: Record<string, unknown>) => void;
    /** TikTok Pixel */
    ttq?: { track: (event: string, params?: Record<string, unknown>) => void };
    /** Pinterest Tag */
    pintrk?: (command: string, event?: string, params?: Record<string, unknown>) => void;
    /** Twitter/X Pixel */
    twq?: (command: string, event: string, params?: Record<string, unknown>) => void;
  }
}

